import { BuildNode, BuildTypes } from "@common/build/types";
import { useCallback, useEffect, useState } from "react";
import { componentWrapperMap } from "./map";
import classNames from "classnames";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { getCurrentLayer, getCurrentRoot, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { useTranslation } from "react-i18next";
import { selectNode, setConditionalVariationMode, updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { openLeftSidebar, setLeftInstance } from "@builder/store/slices/sidebarSlice";
import { endDragging, startDragging, whileDraggingOverMe } from "./DnD/functions";
import PickLayout from "./PickLayout";
import { DynamicDataSectionProvider } from "@common/Contexts/DynamicDataTypeContext";
import SelectedMenu from "./SelectedMenu";

interface RenderFromNodeProps {
    node?: BuildNode | null;
    disabled?: boolean;
    noContext?: boolean;
    noSelectedMenu?: boolean;
}

const RenderFromNode = ({ node, disabled, noContext, noSelectedMenu }: RenderFromNodeProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const currentLayer = useAppSelector(getCurrentLayer);
    const rootId = useAppSelector(getCurrentRoot);
    const selectedNodeId = useAppSelector(getSelectedNode)?.id;
    const activeNode = useAppSelector(state => state.builder.activeNode);
    const buildType = useAppSelector(state => state.builder.build.buildType);
    const showContainer = useAppSelector(state => state.systemUI.containerOutline);
    const draggedUIComponent = useAppSelector(state => state.builder.draggedUIComponent);
    const storeData = useAppSelector(state => state.storeData.data);
    const companyKey = storeData?.company?._id;
    const frameSize = useAppSelector(state => state.builder.frameSize);
    const nodeStates = useAppSelector(state => state.builder.nodeStates);
    const sidebars = useAppSelector(state => state.sidebar);
    const bsk = useAppSelector(state => state.buildSettingsKey);
    const conditionalVariationMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const buildSettingsKey = Object.fromEntries(Object.entries(bsk).map(([key, value]) => [key, value.placeholder || ""]));
    const [isStartFromScratch, setIsStartFromScratch] = useState(false);

    useEffect(() => {
        setIsStartFromScratch(false);
    }, [currentLayer.id]);

    const clickCallback = useCallback(
        (e: React.MouseEvent, id: string) => {
            const [nodeId, index] = id.split("|");
            if (e.button === 0) {
                if (selectedNodeId !== nodeId || (index && parseInt(index) !== conditionalVariationMode.index)) {
                    dispatch(selectNode(nodeId));
                    if (conditionalVariationMode.id && index) {
                        dispatch(setConditionalVariationMode({ id: nodeId, index: parseInt(index) }));
                    }
                }
                if (sidebars.leftSidebar.instance !== "editElement") {
                    dispatch(setLeftInstance("editElement"));
                }
                if (!sidebars.leftSidebar.isOpen) {
                    dispatch(openLeftSidebar());
                }
                e.stopPropagation();
            }
        },
        [dispatch, selectedNodeId, sidebars.leftSidebar, conditionalVariationMode]
    );

    const inputCallback = (value: string) => {
        dispatch(updateNodeProperty({ propertyPath: ["text"], value }));
    };

    const renderNode = useCallback(
        (node: BuildNode, disabled?: boolean) => {
            const modifiedNode = { ...node };

            if (modifiedNode.name === "globalBlockPrint" && frameSize.y === "fit-content" && !modifiedNode.children?.length) {
                if (!modifiedNode.attr?.style?.height) {
                    modifiedNode.attr = {
                        ...modifiedNode.attr,
                        style: {
                            ...modifiedNode.attr?.style,
                            height: "500px"
                        }
                    };
                }
            }

            const [baseId, index] = modifiedNode.id.split("|");

            modifiedNode.attr = {
                ...modifiedNode.attr,
                ...(disabled ? { style: { ...modifiedNode.attr?.style, pointerEvents: "none", opacity: 0.5 } } : {}),
                className: classNames(
                    {
                        [`${modifiedNode.attr?.className}`]: !!modifiedNode.attr?.className,
                        ["builder-selected"]:
                            modifiedNode.id === selectedNodeId || (baseId === selectedNodeId && parseInt(index) === conditionalVariationMode.index)
                    },
                    "builder-hover"
                )
            };
            const Component = componentWrapperMap[node.name as keyof typeof componentWrapperMap];

            const childrenNodes = node.children?.reduce((acc: React.ReactNode[], childId) => {
                if (currentLayer.components[childId]) {
                    acc.push(renderNode(currentLayer.components[childId], disabled));
                }
                return acc;
            }, []);

            return (
                <Component
                    node={modifiedNode}
                    key={node.id}
                    t={t}
                    builderProps={{
                        selectCallback: clickCallback,
                        onDragOverCapture: whileDraggingOverMe,
                        onDragStart: startDragging,
                        onDragEnd: endDragging,
                        active: activeNode === node.id,
                        containerDisabled: !showContainer,
                        ...(nodeStates[node.id] ? { nodeState: nodeStates[node.id] } : {}),
                        ...(node.name === "text" ? { inputCallback } : {})
                    }}
                    storeData={storeData}
                    fromBuilder
                    hubDynamicData={buildSettingsKey ? { buildSettingsKey } : undefined}
                >
                    {childrenNodes}
                </Component>
            );
        },
        [
            currentLayer.components,
            frameSize.y,
            showContainer,
            t,
            activeNode,
            storeData,
            clickCallback,
            nodeStates,
            selectedNodeId,
            conditionalVariationMode
        ]
    );

    const handleClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            dispatch(setLeftInstance("addElement"));
            dispatch(selectNode(null));
        }
    };

    if (!node && !currentLayer.components[rootId]) return null;

    if (
        !isStartFromScratch &&
        buildType !== BuildTypes.GLOBAL_BLOCK &&
        buildType !== BuildTypes.ORG_GLOBAL_BLOCK &&
        buildType !== BuildTypes.OLIVER_GLOBAL_BLOCK &&
        (!currentLayer.components[rootId].children || currentLayer.components[rootId].children?.length === 0) &&
        !draggedUIComponent
    ) {
        return <PickLayout isStartFromScratch={isStartFromScratch} setIsStartFromScratch={setIsStartFromScratch} />;
    }

    if (noContext) {
        return (
            <>
                {/* <div
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2px 0",
                        ...(disabled ? { opacity: 0.5, pointerEvents: "none" } : {})
                    }}
                    onClick={handleClick}
                > */}
                {renderNode(node || currentLayer.components[rootId], disabled)}
                {/* </div> */}
                {selectedNodeId && !noSelectedMenu && <SelectedMenu />}
            </>
        );
    }

    return (
        <>
            <DynamicDataSectionProvider
                value={{
                    dynamicDataScope: "general",
                    dynamicDataObject: { company: storeData?.company, employee: storeData?.employee, user: storeData?.user }
                }}
            >
                <div
                    key={companyKey}
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2px 0",
                        ...(disabled ? { opacity: 0.5, pointerEvents: "none" } : {})
                    }}
                    onClick={handleClick}
                >
                    {renderNode(node || currentLayer.components[rootId], disabled)}
                </div>
                {selectedNodeId && !noSelectedMenu && <SelectedMenu />}
            </DynamicDataSectionProvider>
        </>
    );
};

export default RenderFromNode;
